import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TaxBindersApi,
  BinderStage,
  QuestionnaireApi,
  type TaxBinderPackageExportDto,
  type TaxBinderDto,
  type CreateBinderWithBundleResultDto,
  type OrganizerAnalysisResultDto,
  type RecallReturnDto,
  type RecallReturnResultDto,
  type ReleaseToClientDto,
  type ReleaseToClientResultDto,
  type ReleaseStatusDto,
  type UpdateTaxBinderDto,
} from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { useAuthState } from '@/hooks/auth/useAuth';
import type { BinderFilters } from '@/types/api';
import type { QuestionnaireState } from '@/types/questionnaire.types';
import {
  taxBinderQueryOptions,
  taxBinderKeys,
  documentKeys,
  bundleDocumentKeys,
  taxReturnKeys,
} from '@/lib/query/options';
import { documentRequestKeys } from '@/hooks/api/useDocumentRequests';
import { parseContentDispositionFilename } from '@/utils/fileUtils';

// Re-export for backward compatibility with existing invalidation patterns
export { taxBinderKeys } from '@/lib/query/options';

/**
 * Hook for fetching binders with improved query options integration
 * @param params - Filter and sorting parameters
 * @param options - Additional query options (e.g., refetchInterval for auto-refresh, activeClientId for client-specific caching)
 * @returns Query result with TaxBinderDto[] from API client
 */
export function useTaxBinders(
  params: BinderFilters = {},
  options?: { refetchInterval?: number; activeClientId?: string; enabled?: boolean }
) {
  const { data: authState } = useAuthState();

  const authEnabled = !!authState?.isAuthenticated && !!authState?.token;

  return useQuery({
    ...taxBinderQueryOptions.list(params, authState?.token || undefined, options?.activeClientId),
    enabled: options?.enabled !== undefined ? options.enabled && authEnabled : authEnabled,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook for fetching a single binder by ID
 * @returns Query result with TaxBinderDto from API client
 */
export function useTaxBinder(id: string) {
  const { data: authState } = useAuthState();

  return useQuery({
    ...taxBinderQueryOptions.detail(id, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && !!id,
  });
}

export interface CreateBinderParams {
  name: string;
  taxYear: number;
  clientId: string;
  preparerId?: string;
  dueDate?: string;
  notificationDaysPrior?: number;
  binderPdf?: File;
  engagementLetterPdf?: File;
  extractEngagementLetter?: boolean;
  sendWelcomeEmail?: boolean;
}

export function useCreateTaxBinder() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  // Create a special API config for multipart/form-data requests (no Content-Type header)
  const multipartApiConfig = createApiConfig(authState?.token || undefined);
  // Remove the JSON content-type for multipart requests
  if (multipartApiConfig.baseOptions?.headers) {
    delete (multipartApiConfig.baseOptions.headers as Record<string, string>)['Content-Type'];
  }

  const bindersApi = new TaxBindersApi(multipartApiConfig, undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (binderData: CreateBinderParams) => {
      // Ensure required fields are present and valid
      if (!binderData.name || !binderData.taxYear || !binderData.clientId) {
        throw new Error('Missing required fields: name, taxYear, or clientId');
      }

      // Build the request with all parameters
      const response = await bindersApi.apiTaxBindersPost({
        name: binderData.name,
        taxYear: binderData.taxYear,
        clientId: binderData.clientId,
        preparerId: binderData.preparerId,
        dueDate: binderData.dueDate,
        notificationDaysPrior: binderData.notificationDaysPrior,
        organizerPdf: binderData.binderPdf, // Note: API still uses organizerPdf parameter name
        engagementLetterPdf: binderData.engagementLetterPdf,
        extractEngagementLetter: binderData.extractEngagementLetter,
        sendWelcomeEmail: binderData.sendWelcomeEmail,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch binder lists
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

export interface AnalyzeOrganizerParams {
  organizerPdf: File;
  extractEngagementLetter?: boolean;
  clientOperationId?: string;
}

/**
 * Analyzes an organizer PDF against an existing binder. Extracts tax document
 * requests, and optionally the engagement letter, via the backend's AI pipeline.
 */
export function useAnalyzeOrganizerPdf(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  const multipartApiConfig = createApiConfig(authState?.token || undefined);
  if (multipartApiConfig.baseOptions?.headers) {
    delete (multipartApiConfig.baseOptions.headers as Record<string, string>)['Content-Type'];
  }

  const bindersApi = new TaxBindersApi(multipartApiConfig, undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (args: AnalyzeOrganizerParams): Promise<OrganizerAnalysisResultDto> => {
      const response = await bindersApi.apiTaxBindersBinderIdAnalyzeOrganizerPost({
        binderId,
        organizerPdf: args.organizerPdf,
        extractEngagementLetter: args.extractEngagementLetter ?? false,
        ...(args.clientOperationId && { clientOperationId: args.clientOperationId }),
      });
      // response.data is OrganizerAnalysisResultDtoApiResponse; unwrap to the DTO.
      // Throw on empty body so the dialog's error state triggers instead of rendering
      // a misleading "success with 0 counts" summary (Copilot PR #618 #2).
      const data = response.data?.data;
      if (!data) {
        throw new Error('Unexpected empty response from analyze-organizer');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      // Analyze-organizer creates two documents on the binder (extracted EL + the
      // organizer PDF itself, both with Direction=SentToClient) — these need to
      // appear in the Firm Documents tab without a hard refresh.
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

export function useUpdateTaxBinder(id: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (binderData: Pick<UpdateTaxBinderDto, 'name' | 'dueDate' | 'notificationDaysPrior'>) => {
      const response = await bindersApi.apiTaxBindersIdPut({
        id,
        updateTaxBinderDto: binderData,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate binder detail and lists
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

export function useDeleteTaxBinder() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await bindersApi.apiTaxBindersIdDelete({ id });
      return response.data;
    },
    onSuccess: (_, id) => {
      // Remove from detail cache and invalidate lists
      queryClient.removeQueries({ queryKey: taxBinderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for marking questionnaire as complete
 * Calls the QuestionnaireApi endpoint to set isQuestionnaireComplete flag
 * @param binderId - The binder ID
 * @returns Mutation hook for marking questionnaire complete
 */
export function useMarkQuestionnaireComplete(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const questionnaireApi = new QuestionnaireApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (questionnaireData?: Partial<QuestionnaireState>) => {
      const response = await questionnaireApi.apiTaxBindersBinderIdQuestionnaireCompletePut({
        binderId,
        completeQuestionnaireRequest: {
          answers: questionnaireData || {},
        },
      });
      // Response is BinderDtoApiResponse with structure: { success?, data?, error?, meta? }
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail query to refresh data with updated isQuestionnaireComplete flag
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      // Also invalidate binder lists in case completion affects filtering
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for fetching binders filtered by client ID
 * @returns Query result with TaxBinderDto[] from API client
 */
export function useTaxBindersByClient(clientId: string) {
  const { data: authState } = useAuthState();

  return useQuery({
    ...taxBinderQueryOptions.byClient(clientId, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && !!clientId,
  });
}

/**
 * Hook for fetching binders filtered by tax year
 * @returns Query result with TaxBinderDto[] from API client
 */
export function useBindersByTaxYear(taxYear: number) {
  const { data: authState } = useAuthState();

  return useQuery({
    ...taxBinderQueryOptions.byTaxYear(taxYear, authState?.token || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && !!taxYear,
  });
}

/**
 * Hook for updating a binder's processing stage
 * Uses the API client's apiTaxBindersIdStagePatch endpoint
 *
 * @returns Mutation result with stage update functionality
 * @example
 * ```tsx
 * const updateStage = useUpdateTaxBinderStage();
 *
 * await updateStage.mutateAsync({
 *   binderId: '123',
 *   stage: BinderStage.Preprocessing,
 * });
 * ```
 */
export function useUpdateTaxBinderStage() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async ({ binderId, stage }: { binderId: string; stage: BinderStage }) => {
      // Call the stage update API endpoint
      // response.data is ApiResponse<TaxBinderDto>
      // response.data.data is the actual TaxBinderDto
      const response = await bindersApi.apiTaxBindersIdStagePatch({
        id: binderId,
        updateTaxBinderStageDto: { stage },
      });
      return response.data?.data || null;
    },
    onSuccess: (_, { binderId }) => {
      // Invalidate binder detail and lists to refetch updated data
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/** Result from binder package export containing file data and filename */
export interface BinderExportResult {
  /** The exported file as a Blob */
  blob: Blob;
  /** Filename from Content-Disposition header, or null if not provided */
  filename: string | null;
  /** File names that failed to be included in the export */
  failedFiles: string[];
}

/**
 * Hook for exporting binder package with client data and document filtering
 * Returns a Blob (PDF or ZIP) with filename from Content-Disposition header
 * @returns Mutation hook for exporting binder package
 */
export function useExportBinderPackage() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async ({
      binderId,
      exportData,
    }: {
      binderId: string;
      exportData: TaxBinderPackageExportDto;
    }): Promise<BinderExportResult> => {
      const response = await bindersApi.apiTaxBindersIdExportPackagePost(
        {
          id: binderId,
          taxBinderPackageExportDto: exportData,
        },
        {
          responseType: 'blob',
        }
      );

      // Extract filename from Content-Disposition header
      // The backend returns: attachment; filename="TaxPackage_Name_Year.zip"; filename*=UTF-8''...
      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const filename = parseContentDispositionFilename(contentDisposition);

      // Extract failed files from X-Export-Failures header
      let failedFiles: string[] = [];
      const failedFilesHeader = response.headers?.['x-export-failures'] as string | undefined;
      if (failedFilesHeader) {
        try {
          const parsed: unknown = JSON.parse(failedFilesHeader);
          if (Array.isArray(parsed)) {
            failedFiles = parsed.filter((item): item is string => typeof item === 'string');
          }
        } catch {
          // If parsing fails, ignore - don't block the download
        }
      }

      // The response.data is a Blob (PDF or ZIP file)
      // API client returns void type, but with responseType: 'blob', Axios returns Blob
      return {
        blob: response.data as unknown as Blob,
        filename,
        failedFiles,
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refresh export status on documents
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(variables.binderId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

/**
 * Hook for submitting a binder for preparer review ("Ready to Proceed")
 * Marks the binder as client-submitted with optional note
 *
 * @param binderId - The binder ID to submit
 * @returns Mutation hook for submitting binder
 *
 * @example
 * ```tsx
 * const submitForReview = useSubmitBinderForReview('binder-123');
 *
 * await submitForReview.mutateAsync({
 *   note: 'All documents uploaded, ready for your review',
 * });
 * ```
 */
export function useSubmitBinderForReview(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async ({ note }: { note?: string } = {}): Promise<TaxBinderDto | null> => {
      const response = await bindersApi.apiTaxBindersIdSubmitPost({
        id: binderId,
        submitTaxBinderRequest: { note },
      });
      // API returns BinderDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the TaxBinderDto
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail and lists to refresh with updated submission status
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for withdrawing a binder submission ("Undo Ready to Proceed")
 * Clears the client submission status
 *
 * @param binderId - The binder ID to withdraw submission
 * @returns Mutation hook for withdrawing submission
 *
 * @example
 * ```tsx
 * const withdrawSubmission = useWithdrawBinderSubmission('binder-123');
 *
 * await withdrawSubmission.mutateAsync();
 * ```
 */
export function useWithdrawBinderSubmission(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (): Promise<TaxBinderDto | null> => {
      const response = await bindersApi.apiTaxBindersIdWithdrawSubmissionPost({
        id: binderId,
      });
      // API returns BinderDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the TaxBinderDto
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail and lists to refresh with cleared submission status
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
    },
  });
}

/**
 * Hook for resending the welcome email to the client for a binder
 *
 * @param binderId - The binder ID to resend welcome email for
 * @returns Mutation hook for resending the welcome email
 *
 * @example
 * ```tsx
 * const resendWelcomeEmail = useResendBinderWelcomeEmail('binder-123');
 *
 * await resendWelcomeEmail.mutateAsync();
 * ```
 */
export function useResendBinderWelcomeEmail(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (): Promise<boolean> => {
      const response = await bindersApi.apiTaxBindersIdResendWelcomeEmailPost({ id: binderId });
      // API returns BooleanApiResponse with structure: { success?, data?, error?, meta? }
      return response.data?.data || false;
    },
    onSuccess: () => {
      // No need to invalidate queries since resending email doesn't change binder data
      // But we can still invalidate to trigger a refetch if needed
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
    },
  });
}

/**
 * Hook for clearing the late documents flag on a binder
 *
 * Clears the LateDocumentsUploadedAt and LateDocumentsCount fields on a binder,
 * indicating the preparer has reviewed the late document uploads.
 *
 * @param binderId - The binder ID to clear late documents flag for
 * @returns Mutation hook for clearing late documents
 *
 * @example
 * ```tsx
 * const clearLateDocuments = useClearLateDocuments('binder-123');
 *
 * await clearLateDocuments.mutateAsync();
 * ```
 */
export function useClearLateDocuments(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (): Promise<TaxBinderDto | null> => {
      const response = await bindersApi.apiTaxBindersIdClearLateDocumentsPost({ id: binderId });
      // API returns BinderDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the TaxBinderDto
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail and lists to refresh with cleared late documents
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // Invalidate document queries to refresh isLate flags on individual documents
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      // Also invalidate dashboard stats since late documents count changes
      queryClient.invalidateQueries({ queryKey: ['preparerDashboard'] });
    },
  });
}

/**
 * Parameters for creating a binder with a tax return bundle in one step
 */
export interface CreateBinderWithBundleParams {
  /** Client ID to create the binder for */
  clientId: string;
  /** Tax year (2000-2100) */
  taxYear: number;
  /** Binder display name (max 200 chars) */
  name: string;
  /** Filing deadline (optional) */
  dueDate?: string;
  /** Client copy PDF file (required) */
  clientCopyPdf: File;
  /** Government copy PDF file (required) */
  governmentCopyPdf: File;
  /** K-1 PDF file (optional) */
  k1Pdf?: File;
  /** Optional invoice link URL to associate with this binder */
  invoiceLink?: string;
  /** Whether the engagement letter has already been signed outside the system */
  engagementLetterAlreadySigned?: boolean;
}

/**
 * Hook for creating a binder with a tax return bundle in one step
 *
 * This endpoint creates a binder and immediately processes the uploaded bundle,
 * extracting tax returns, filing instructions, and payment vouchers.
 *
 * @returns Mutation hook with CreateBinderWithBundleResultDto result
 *
 * @example
 * ```tsx
 * const createWithBundle = useCreateBinderWithBundle();
 *
 * const result = await createWithBundle.mutateAsync({
 *   clientId: 'client-123',
 *   taxYear: 2024,
 *   name: '2024 Tax Return',
 *   clientCopyPdf: clientFile,
 *   governmentCopyPdf: govFile,
 *   k1Pdf: k1File, // optional
 * });
 *
 * // Navigate to the new binder
 * navigate(`/preparer/tax-binders/${result.binder?.id}`);
 * ```
 */
export function useCreateBinderWithBundle() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  // Create a special API config for multipart/form-data requests (no Content-Type header)
  const multipartApiConfig = createApiConfig(authState?.token || undefined);
  // Remove the JSON content-type for multipart requests
  if (multipartApiConfig.baseOptions?.headers) {
    delete (multipartApiConfig.baseOptions.headers as Record<string, string>)['Content-Type'];
  }

  const bindersApi = new TaxBindersApi(multipartApiConfig, undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (params: CreateBinderWithBundleParams): Promise<CreateBinderWithBundleResultDto | null> => {
      // Validate required fields
      if (!params.clientId || !params.taxYear || !params.name) {
        throw new Error('Missing required fields: clientId, taxYear, or name');
      }
      if (!params.clientCopyPdf || !params.governmentCopyPdf) {
        throw new Error('Missing required files: clientCopyPdf or governmentCopyPdf');
      }

      const response = await bindersApi.apiTaxBindersWithBundlePost({
        clientId: params.clientId,
        taxYear: params.taxYear,
        name: params.name,
        dueDate: params.dueDate,
        invoiceLink: params.invoiceLink,
        clientCopyPdf: params.clientCopyPdf,
        governmentCopyPdf: params.governmentCopyPdf,
        k1Pdf: params.k1Pdf,
        engagementLetterAlreadySigned: params.engagementLetterAlreadySigned,
      });

      return response.data?.data || null;
    },
    onSuccess: (data) => {
      // Invalidate binder lists
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // If we have the new binder ID, also set it in cache
      if (data?.binder?.id) {
        queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(data.binder.id) });
      }
      // Invalidate document queries since bundle creates documents
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

/**
 * Hook for releasing a binder's tax returns to the client for review and e-signature
 *
 * This action:
 * - Sends signature requests for all 8879 e-file authorization forms (bundle signing)
 * - Sends signature request for Direct Deposit Report (if present)
 * - Transitions binder to FinalClientReview stage
 * - Notifies the client via real-time SignalR and batched email/SMS
 *
 * @param binderId - The binder ID to release
 * @returns Mutation hook with ReleaseToClientResultDto result
 *
 * @example
 * ```tsx
 * const releaseToClient = useReleaseToClient('binder-123');
 *
 * const result = await releaseToClient.mutateAsync({
 *   customMessage: 'Your tax returns are ready for review!',
 * });
 *
 * // result.jurisdictionsIncluded contains ['Federal', 'KY', ...]
 * // result.notificationsSent contains count of notifications
 * ```
 */
export function useReleaseToClient(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (data?: ReleaseToClientDto): Promise<ReleaseToClientResultDto | null> => {
      const response = await bindersApi.apiTaxBindersIdReleaseToClientPost({
        id: binderId,
        releaseToClientDto: data,
      });
      // API returns ReleaseToClientResultDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the ReleaseToClientResultDto
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail to refresh with updated stage and release status
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      // Invalidate binder lists since stage changed to FinalClientReview
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // Invalidate release status query
      queryClient.invalidateQueries({ queryKey: ['taxBinder', binderId, 'releaseStatus'] });
      // Invalidate bundle documents (signature statuses update after release)
      queryClient.invalidateQueries({ queryKey: bundleDocumentKeys.list(binderId) });
      // Invalidate tax return list (jurisdiction signature state may change)
      queryClient.invalidateQueries({ queryKey: taxReturnKeys.list(binderId) });
    },
  });
}

/**
 * Hook for recalling a tax return that has been released to a client
 *
 * This action:
 * - Reverts the binder stage to SentForPreparation
 * - Soft-deletes bundle documents
 * - Archives active signature requests
 * - Notifies the client of the recall
 *
 * @param binderId - The binder ID to recall
 * @returns Mutation hook with RecallReturnResultDto result
 */
export function useRecallReturn(binderId: string) {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (data: RecallReturnDto): Promise<RecallReturnResultDto | null> => {
      const response = await bindersApi.apiTaxBindersIdRecallReturnPost({
        id: binderId,
        recallReturnDto: data,
      });
      // API returns RecallReturnResultDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the RecallReturnResultDto
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate binder detail to refresh with reverted stage and recall info
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
      // Invalidate binder lists since stage changed
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // Invalidate release status since binder is no longer released
      queryClient.invalidateQueries({ queryKey: ['taxBinder', binderId, 'releaseStatus'] });
      // Invalidate bundle documents (soft-deleted after recall)
      queryClient.invalidateQueries({ queryKey: bundleDocumentKeys.list(binderId) });
      // Invalidate tax return list (signature state changes)
      queryClient.invalidateQueries({ queryKey: taxReturnKeys.list(binderId) });
    },
  });
}

/**
 * Hook for fetching the release status of a binder
 *
 * Returns information about whether the binder has been released to the client
 * and the current status of signature collection.
 *
 * @param binderId - The binder ID to check release status for
 * @param options - Query options (enabled, refetchInterval, etc.)
 * @returns Query result with ReleaseStatusDto
 *
 * @example
 * ```tsx
 * const { data: releaseStatus } = useReleaseStatus('binder-123');
 *
 * if (releaseStatus?.isReleased) {
 *   console.log(`${releaseStatus.signaturesCompleted} of ${releaseStatus.signaturesRequired} signatures completed`);
 * }
 * ```
 */
export function useReleaseStatus(binderId: string, options?: { enabled?: boolean; refetchInterval?: number | false }) {
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useQuery({
    queryKey: ['taxBinder', binderId, 'releaseStatus'] as const,
    queryFn: async (): Promise<ReleaseStatusDto | null> => {
      const response = await bindersApi.apiTaxBindersIdReleaseStatusGet({ id: binderId });
      // API returns ReleaseStatusDtoApiResponse with structure: { success?, data?, error?, meta? }
      // We unwrap and return just the data field which contains the ReleaseStatusDto
      return response.data?.data || null;
    },
    enabled: (options?.enabled ?? true) && !!authState?.isAuthenticated && !!authState?.token && !!binderId,
    refetchInterval: options?.refetchInterval,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook for downloading the signed document package from DocuSeal
 * Uses GET /api/binders/{id}/download-signed which returns the signed PDF binary
 */
export function useDownloadSignedDocument() {
  const { data: authState } = useAuthState();
  const bindersApi = new TaxBindersApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async ({ binderId, fileName }: { binderId: string; fileName?: string }) => {
      const response = await bindersApi.apiTaxBindersIdDownloadSignedGet({ id: binderId }, { responseType: 'blob' });

      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const serverFilename = parseContentDispositionFilename(contentDisposition);

      return {
        blob: response.data as unknown as Blob,
        filename: fileName || serverFilename || 'Signed-Tax-Return.pdf',
      };
    },
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
